import { Controller } from "@hotwired/stimulus";
import {
  calculateMeasure,
  formatComputed,
  formatInput,
  fromDisplay,
  measureNumber,
  toDisplay,
} from "../reconstitution.js";
import { placeRuler } from "../syringe_ruler.js";

const SYRINGE_LABELS = {
  "true,true": "Exceeds vial and syringe",
  "true,false": "Exceeds vial",
  "false,true": "Exceeds syringe",
  "false,false": "Syringe",
};

// Typing is calculated here. Every other change (a unit, the syringe, the mode, a row)
// submits the form so the server redraws it, which relies on the hidden "shown" fields
// holding what was last on screen, so they are rewritten after every calculation.
export default class extends Controller {
  static outlets = ["calculator-example", "calculator-water-comparison"];
  static targets = [
    "syringeLabel",
    "notice",
    "largerSyringe",
    "supply",
    "message",
    "fullDosesRow",
    "fullDoses",
    "fullDosesUnit",
    "calculate",
    "drawReadout",
    "drawWarning",
    "mathLink",
  ];

  connect() {
    this.calculateTarget.hidden = true;
    this.restoreFocus();
  }

  input(event) {
    if (event.target.type !== "text") return;

    const name = event.target.name;
    if (this.typedDuringRedraw) {
      this.typedDuringRedraw = [...this.typedDuringRedraw.filter((typed) => typed !== name), name];
    }
    const [, index, key] = name.match(/^calculator(?:\[compounds\]\[(\d+)\])?\[(\w+)\]$/) ?? [];
    if (key === "draw") this.field("target").value = "volume";
    if (key === "total") this.field("target").value = "total";
    if (key === "dose") this.field("target").value = `component:${this.field("compounds", index, "id").value}`;

    this.clearError(event.target);
    this.recalculate();
  }

  completeModeSwitch() {
    if (this.field("pending_mode").value) this.element.requestSubmit();
  }

  // The server's response replaces the form, and Turbo only restores focus to an
  // autofocus element, so the focused control is found again by name once it lands.
  // Focus is read again just before the redraw because leaving a field can submit,
  // and the browser reports no focused field at that moment. Text typed while the
  // response was on its way is carried into the new form so it is not lost.
  rememberFocus(event) {
    const submitter = event.submitter && !event.submitter.hidden ? event.submitter : null;
    const element = submitter ?? document.activeElement;
    this.typedDuringRedraw = [];
    this.focusAfterRedraw =
      element?.form === this.element && element.name ? { name: element.name, value: submitter?.value ?? null } : null;
  }

  rememberTyping(event) {
    const frame = this.element.closest("turbo-frame");
    if (event.target !== frame || !this.typedDuringRedraw) return;

    const active = document.activeElement;
    const focus =
      active?.form === this.element && active.type === "text"
        ? { name: active.name, value: null }
        : this.focusAfterRedraw;
    const typed = this.typedDuringRedraw.map((name) => [name, this.element.elements.namedItem(name).value]);
    frame.dataset.measureFocus = JSON.stringify({ ...focus, typed });
  }

  restoreFocus() {
    const frame = this.element.closest("turbo-frame");
    if (!frame?.dataset.measureFocus) return;

    const { name, value, typed } = JSON.parse(frame.dataset.measureFocus);
    delete frame.dataset.measureFocus;
    typed.forEach(([typedName, text]) => {
      const input = this.element.elements.namedItem(typedName);
      if (!input) return;

      input.value = text;
      this.input({ target: input });
    });
    const element = Array.from(this.element.elements).find(
      (candidate) => candidate.name === name && (value === null || candidate.value === value)
    );
    element?.focus();
    if (element?.type === "text") element.setSelectionRange(element.value.length, element.value.length);
  }

  recalculate() {
    const state = this.readState();
    const result = calculateMeasure(state);

    this.writeDoseFields(state, result);
    this.writeOutputs(state, result);
    this.rememberShown();
    this.updateExample(state, result);
    this.updateWaterComparison(state, result);
  }

  calculatorExampleOutletConnected(outlet) {
    const state = this.readState();
    this.updateExample(state, calculateMeasure(state), outlet);
  }

  updateExample(state, result, outlet = null) {
    const example = outlet ?? (this.hasCalculatorExampleOutlet ? this.calculatorExampleOutlet : null);
    if (!example) return;

    example.update(state, result, this.displayFields(state), this.notice(state, result));
    if (this.hasMathLinkTarget) this.mathLinkTarget.hidden = !result.ready;
  }

  calculatorWaterComparisonOutletConnected(outlet) {
    const state = this.readState();
    this.updateWaterComparison(state, calculateMeasure(state), outlet);
  }

  updateWaterComparison(state, result, outlet = null) {
    const comparison =
      outlet ?? (this.hasCalculatorWaterComparisonOutlet ? this.calculatorWaterComparisonOutlet : null);
    comparison?.update(state, result, this.displayFields(state));
  }

  displayFields(state) {
    return this.compoundIndexes().map((index) => ({
      quantity: this.field("compounds", index, "unit").value,
      quantityText: this.field("compounds", index, "quantity").value,
      dose:
        state.compounds.length === 1
          ? this.field("total_unit").value
          : this.field("compounds", index, "dose_unit").value,
      doseText: state.compounds.length === 1 ? this.field("total").value : this.field("compounds", index, "dose").value,
    }));
  }

  readState() {
    const [kind, id] = this.field("target").value.split(":");

    return {
      entryMode: this.field("entry_mode").value,
      volumeMl: measureNumber(this.field("water").value),
      syringeMl: Number(this.field("syringe").value),
      compounds: this.compoundIndexes().map((index) => ({
        id: this.field("compounds", index, "id").value,
        quantity: this.amount(["compounds", index], "quantity", "unit"),
      })),
      target: { kind: ["volume", "component"].includes(kind) ? kind : "total", id, value: this.heldValue(kind, id) },
    };
  }

  heldValue(kind, id) {
    if (kind === "volume") return this.amount([], "draw", "draw_unit");
    if (kind !== "component") return this.amount([], "total", "total_unit");

    const index = this.compoundIndexes().find((candidate) => this.field("compounds", candidate, "id").value === id);
    return index === undefined ? null : this.amount(["compounds", index], "dose", "dose_unit");
  }

  writeDoseFields(state, result) {
    const { kind, id } = state.target;
    if (kind !== "volume") this.write([], "draw", "draw_unit", result.volumeMl);
    if (kind !== "total") this.write([], "total", "total_unit", result.totalDose);
    state.compounds.forEach((compound, index) => {
      if (this.field("compounds", index, "dose") && !(kind === "component" && id === compound.id)) {
        this.write(["compounds", index], "dose", "dose_unit", result.doses[index] ?? null);
      }
    });
  }

  writeOutputs(state, result) {
    const syringeLabel = SYRINGE_LABELS[`${result.exceedsVial},${result.exceedsSyringe}`];
    this.syringeLabelTarget.textContent =
      result.exceedsVial || result.exceedsSyringe
        ? syringeLabel
        : (this.syringeLabelTarget.dataset.defaultLabel ?? syringeLabel);
    this.show(this.supplyTarget, this.supplySummary(state, result));
    this.show(this.noticeTarget, this.notice(state, result));
    this.show(this.largerSyringeTarget, result.largerSyringe && `Use a ${volumeText(result.largerSyringe)} syringe`);
    if (result.largerSyringe) this.largerSyringeTarget.value = `syringe:${result.largerSyringe}`;
    this.fullDosesRowTarget.hidden = result.fullDoses === null;
    this.fullDosesTarget.textContent = result.fullDoses ?? "";
    if (this.hasFullDosesUnitTarget) {
      this.fullDosesUnitTarget.textContent = result.fullDoses === 1 ? "full dose" : "full doses";
    }
    this.messageTarget.textContent = this.message(state, result);
    this.messageTarget.classList.toggle("text-ink", result.ready);
    this.messageTarget.classList.toggle("text-ink-muted", !result.ready);
    this.moveRuler(result.volumeMl ?? 0, state.syringeMl);
    if (this.hasDrawReadoutTarget) this.writeReadout(result, syringeLabel);
  }

  writeReadout(result, syringeLabel) {
    const units = formatComputed(toDisplay(result.volumeMl, "units"), { grouping: true });
    const exceeds = result.exceedsSyringe || result.exceedsVial;
    this.drawReadoutTarget.textContent = result.ready
      ? `${units} ${unitsWord(units)} · ${amountText(result.volumeMl, "mL")}`
      : "";
    this.drawReadoutTarget.classList.toggle("text-amber-700", exceeds);
    this.drawReadoutTarget.classList.toggle("text-ink", !exceeds);
    this.show(this.drawWarningTarget, exceeds && syringeLabel);
  }

  supplySummary(state, result) {
    const unit = this.supplyUnit();
    if (state.entryMode === "amount") return result.concentration && `${amountText(result.concentration, unit)}/mL`;

    return result.totalInVial && `${amountText(result.totalInVial, unit)} in ${volumeText(state.volumeMl)}`;
  }

  notice(state, result) {
    const exceeded = [];
    if (result.exceedsSyringe) exceeded.push(`the ${volumeText(state.syringeMl)} syringe`);
    if (result.exceedsVial) exceeded.push(`the ${volumeText(state.volumeMl)} in the vial`);

    return exceeded.length > 0 ? `This draw exceeds ${exceeded.join(" and ")}.` : null;
  }

  message(state, result) {
    if (result.ready) return this.heldDescription(state);
    if (result.supplyReady) return "Enter a dose or a draw amount to see the rest.";

    return state.entryMode === "strength"
      ? "Enter the strength to see the draw."
      : "Enter the amount and volume to see the draw.";
  }

  heldDescription(state) {
    const { kind, id, value } = state.target;
    if (kind === "volume") return "The calculation keeps your draw volume fixed.";
    if (kind !== "component") return "The calculation keeps your total dose fixed.";

    const index = state.compounds.findIndex((compound) => compound.id === id);
    return `The calculation keeps Compound ${index + 1} at ${amountText(value, this.field("compounds", index, "dose_unit").value)}.`;
  }

  moveRuler(drawMl, syringeMl) {
    const drawn = drawMl * 100;
    const capacity = syringeMl * 100;
    const drawnText = formatComputed(drawn);
    const label = `${drawnText} ${unitsWord(drawnText)} in a ${formatComputed(capacity)} unit syringe`;
    this.element.querySelectorAll("[data-syringe-ruler]").forEach((ruler) => placeRuler(ruler, drawn, capacity, label));
  }

  rememberShown() {
    this.remember([], "draw", "draw_unit");
    this.remember([], "total", "total_unit");
    this.compoundIndexes().forEach((index) => {
      this.remember(["compounds", index], "quantity", "unit");
      this.remember(["compounds", index], "dose", "dose_unit");
    });
  }

  remember(prefix, name, unit) {
    if (!this.field(...prefix, name)) return;

    this.field(...prefix, `shown_${name}`).value = this.field(...prefix, name).value;
    this.field(...prefix, `shown_${unit}`).value = this.field(...prefix, unit).value;
  }

  clearError(input) {
    const id = input.getAttribute("aria-describedby");
    if (!id) return;

    document.getElementById(id)?.remove();
    input.removeAttribute("aria-describedby");
    input.removeAttribute("aria-invalid");
  }

  show(element, text) {
    element.hidden = !text;
    element.textContent = text || "";
  }

  amount(prefix, name, unit) {
    return fromDisplay(measureNumber(this.field(...prefix, name).value), this.field(...prefix, unit).value);
  }

  write(prefix, name, unit, value) {
    this.field(...prefix, name).value = formatComputed(toDisplay(value, this.field(...prefix, unit).value));
  }

  supplyUnit() {
    const units = new Set(this.compoundIndexes().map((index) => this.field("compounds", index, "unit").value));
    return units.size === 1 ? [...units][0] : "mg";
  }

  compoundIndexes() {
    const indexes = [];
    while (this.field("compounds", indexes.length, "id")) indexes.push(indexes.length);
    return indexes;
  }

  field(...path) {
    return this.element.elements.namedItem(`calculator${path.map((key) => `[${key}]`).join("")}`);
  }
}

function amountText(value, unit) {
  return `${formatComputed(toDisplay(value, unit), { grouping: true })} ${unit}`;
}

function unitsWord(text) {
  return text === "1" ? "unit" : "units";
}

function volumeText(valueMl) {
  return `${formatInput(valueMl)} mL`;
}
